import { useEffect, useState } from 'react'
import './CalendlyWidget.css'

interface CalendlyWidgetProps {
  url: string
  pageSettings?: {
    backgroundColor?: string
    hideEventTypeDetails?: boolean
    hideLandingPageDetails?: boolean
    primaryColor?: string
    textColor?: string
  }
  prefill?: {
    email?: string
    name?: string
    firstName?: string
    lastName?: string
  }
}

declare global {
  interface Window {
    Calendly: {
      initInlineWidget: (options: {
        url: string
        parentElement: HTMLElement
        prefill?: CalendlyWidgetProps['prefill']
        pageSettings?: CalendlyWidgetProps['pageSettings']
      }) => void
    }
  }
}

function CalendlyWidget({ url, pageSettings, prefill }: CalendlyWidgetProps) {
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    // Validate URL
    if (!url || url.includes('your-calendly-username')) {
      setError('Please configure your Calendly URL in the .env file (VITE_CALENDLY_URL)')
      setIsLoading(false)
      return
    }

    setIsLoading(true)
    setError(null)
    // Load Calendly script if not already loaded
    if (!document.querySelector('script[src*="calendly"]')) {
      const script = document.createElement('script')
      script.src = 'https://assets.calendly.com/assets/external/widget.js'
      script.async = true
      document.body.appendChild(script)
    }

    // Initialize Calendly widget when script is loaded
    const initCalendly = () => {
      if (window.Calendly) {
        const calendlyContainer = document.getElementById('calendly-container')
        if (calendlyContainer) {
          try {
            // Clear any existing content
            calendlyContainer.innerHTML = ''
            
            window.Calendly.initInlineWidget({
              url: url,
              parentElement: calendlyContainer,
              prefill: prefill,
              pageSettings: pageSettings || {
                backgroundColor: 'ffffff',
                hideEventTypeDetails: false,
                hideLandingPageDetails: false,
                primaryColor: '1e40af', // Match your primary color
                textColor: '1f2937' // Match your text color
              }
            })
            setIsLoading(false)
          } catch (err) {
            console.error('Error initializing Calendly:', err)
            setError('Failed to load Calendly widget. Please check your Calendly URL.')
            setIsLoading(false)
          }
        }
      } else {
        // Retry if Calendly isn't loaded yet
        setTimeout(initCalendly, 100)
      }
    }

    // Wait for Calendly to be available
    if (window.Calendly) {
      initCalendly()
    } else {
      // Listen for script load
      const checkCalendly = setInterval(() => {
        if (window.Calendly) {
          clearInterval(checkCalendly)
          initCalendly()
        }
      }, 100)

      // Cleanup interval after 10 seconds
      setTimeout(() => {
        clearInterval(checkCalendly)
        if (!window.Calendly) {
          setError('Calendly failed to load. Please check your internet connection and try again.')
          setIsLoading(false)
        }
      }, 10000)
    }

    // Cleanup function
    return () => {
      const calendlyContainer = document.getElementById('calendly-container')
      if (calendlyContainer) {
        calendlyContainer.innerHTML = ''
      }
    }
  }, [url, pageSettings, prefill])

  if (error) {
    return (
      <div className="calendly-error">
        <h3>Unable to Load Scheduling</h3>
        <p>{error}</p>
        <p className="calendly-error-help">
          To fix this, create a <code>.env</code> file in the root directory and add:
          <br />
          <code>VITE_CALENDLY_URL=https://calendly.com/your-username/event-type</code>
        </p>
      </div>
    )
  }

  return (
    <div className="calendly-widget-wrapper">
      {isLoading && (
        <div className="calendly-loading">
          <p>Loading calendar...</p>
        </div>
      )}
      <div id="calendly-container" className="calendly-container"></div>
    </div>
  )
}

export default CalendlyWidget
